import React from 'react';
import { Helmet } from 'react-helmet';

import Header from '../../components/ui/Header';
import Footer from '../home-landing/components/Footer';
import Icon from '../../components/AppIcon';

const PrivacyPolicy = () => {
  const lastUpdated = 'January 28, 2026';

  const sections = [
    {
      id: 'information-collection',
      title: 'Information We Collect',
      icon: 'Database',
      content: [
        {
          subtitle: 'Personal Information',
          text: 'We collect information that you provide directly to us, including your name, email address, and any other information you choose to provide when you contact us, subscribe to our newsletter, or interact with our services.'
        },
        {
          subtitle: 'Usage Information',
          text: 'We automatically collect certain information about your device and how you interact with our website, including your IP address, browser type, operating system, referring URLs, pages viewed, and the dates/times of your visits.'
        },
        {
          subtitle: 'Cookies and Tracking Technologies',
          text: 'We use cookies and similar tracking technologies to collect information about your browsing activities. For detailed information about our cookie practices, please see our Cookie Policy.'
        }
      ]
    },
    {
      id: 'information-use',
      title: 'How We Use Your Information',
      icon: 'Settings',
      content: [
        {
          text: 'We use the information we collect to:',
          list: [
            'Provide, maintain, and improve our services',
            'Send you technical notices, updates, and support messages',
            'Respond to your comments, questions, and requests',
            'Analyze usage patterns and trends to enhance user experience',
            'Detect, prevent, and address technical issues and security threats',
            'Comply with legal obligations and enforce our terms of service'
          ]
        }
      ]
    },
    {
      id: 'information-sharing',
      title: 'Information Sharing and Disclosure',
      icon: 'Share2',
      content: [
        {
          text: 'We do not sell, trade, or rent your personal information to third parties. We may share your information only in the following circumstances:'
        },
        {
          subtitle: 'Service Providers',
          text: 'We may share your information with third-party service providers who perform services on our behalf, such as hosting, analytics, and email delivery. These providers are bound by confidentiality agreements.'
        },
        {
          subtitle: 'Legal Requirements',
          text: 'We may disclose your information if required to do so by law or in response to valid requests by public authorities (e.g., court orders, subpoenas).'
        },
        {
          subtitle: 'Business Transfers',
          text: 'In the event of a merger, acquisition, or sale of assets, your information may be transferred as part of that transaction. We will notify you of any such change.'
        }
      ]
    },
    {
      id: 'data-security',
      title: 'Data Security',
      icon: 'Shield',
      content: [
        {
          text: 'We implement appropriate technical and organizational measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction. These measures include:'
        },
        {
          list: [
            'Encryption of data in transit and at rest using industry-standard protocols',
            'Regular security assessments and vulnerability testing',
            'Access controls and authentication mechanisms',
            'Employee training on data protection and privacy practices',
            'Incident response procedures for data breaches'
          ]
        },
        {
          text: 'However, no method of transmission over the Internet or electronic storage is 100% secure. While we strive to protect your information, we cannot guarantee absolute security.'
        }
      ]
    },
    {
      id: 'your-rights',
      title: 'Your Rights and Choices',
      icon: 'User',
      content: [
        {
          text: 'Depending on your location, you may have certain rights regarding your personal information:'
        },
        {
          subtitle: 'Access and Portability',
          text: 'You have the right to request access to the personal information we hold about you and to receive a copy of that information in a structured, commonly used format.'
        },
        {
          subtitle: 'Correction',
          text: 'You have the right to request that we correct any inaccurate or incomplete personal information we hold about you.'
        },
        {
          subtitle: 'Deletion',
          text: 'You have the right to request that we delete your personal information, subject to certain legal exceptions.'
        },
        {
          subtitle: 'Objection and Restriction',
          text: 'You have the right to object to or request restriction of certain processing of your personal information.'
        },
        {
          subtitle: 'Withdraw Consent',
          text: 'Where we rely on your consent to process your information, you have the right to withdraw that consent at any time.'
        },
        {
          text: 'To exercise any of these rights, please contact us using the information provided at the end of this policy.'
        }
      ]
    },
    {
      id: 'data-retention',
      title: 'Data Retention',
      icon: 'Clock',
      content: [
        {
          text: 'We retain your personal information for as long as necessary to fulfill the purposes outlined in this Privacy Policy, unless a longer retention period is required or permitted by law. When determining retention periods, we consider:'
        },
        {
          list: [
            'The nature and sensitivity of the information',
            'The purposes for which we process the information',
            'Legal, regulatory, and contractual obligations',
            'The potential risk of harm from unauthorized use or disclosure'
          ]
        },
        {
          text: 'When we no longer need your information, we will securely delete or anonymize it in accordance with our data retention policies.'
        }
      ]
    },
    {
      id: 'international-transfers',
      title: 'International Data Transfers',
      icon: 'Globe',
      content: [
        {
          text: 'Your information may be transferred to and processed in countries other than your country of residence. These countries may have data protection laws that differ from those in your country.'
        },
        {
          text: 'When we transfer your information internationally, we implement appropriate safeguards to ensure your information receives an adequate level of protection, including:'
        },
        {
          list: [
            'Standard contractual clauses approved by the European Commission',
            'Privacy Shield certification (where applicable)',
            'Other legally recognized transfer mechanisms'
          ]
        }
      ]
    },
    {
      id: 'children-privacy',
      title: "Children\'s Privacy",
      icon: 'Users',
      content: [
        {
          text: 'Our services are not directed to individuals under the age of 16. We do not knowingly collect personal information from children under 16. If you are a parent or guardian and believe your child has provided us with personal information, please contact us, and we will take steps to delete such information.'
        }
      ]
    },
    {
      id: 'third-party-links',
      title: 'Third-Party Links',
      icon: 'ExternalLink',
      content: [
        {
          text: 'Our website may contain links to third-party websites and services. We are not responsible for the privacy practices or content of these third parties. We encourage you to review the privacy policies of any third-party sites you visit.'
        }
      ]
    },
    {
      id: 'policy-changes',
      title: 'Changes to This Privacy Policy',
      icon: 'FileText',
      content: [
        {
          text: 'We may update this Privacy Policy from time to time to reflect changes in our practices or for legal, regulatory, or operational reasons. We will notify you of any material changes by:'
        },
        {
          list: [
            'Posting the updated policy on this page with a new "Last Updated" date',
            'Sending you an email notification (if you have provided your email address)',
            'Displaying a prominent notice on our website'
          ]
        },
        {
          text: 'Your continued use of our services after any changes indicates your acceptance of the updated Privacy Policy.'
        }
      ]
    }
  ];

  return (
    <>
      <Helmet>
        <title>Privacy Policy - Portfolio</title>
        <meta name="description" content="Learn how we collect, use, and protect your personal information. Comprehensive privacy policy with GDPR compliance and user rights information." />
      </Helmet>
      <Header />
      <div className="min-h-screen bg-background">
        {/* Hero Section */}
        <div className="bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white py-16 md:py-24">
          <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8">
            <div className="max-w-3xl">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-accent/20 rounded-lg flex items-center justify-center">
                  <Icon name="Shield" size={24} color="var(--color-accent)" />
                </div>
                <h1 className="text-4xl md:text-5xl font-bold">Privacy Policy</h1>
              </div>
              <p className="text-lg text-gray-300 mb-4">
                Your privacy is important to us. This policy explains how we collect, use, and protect your personal information.
              </p>
              <p className="text-sm text-gray-400">
                Last Updated: <span className="text-accent">{lastUpdated}</span>
              </p>
            </div>
          </div>
        </div>

        {/* Quick Navigation */}
        <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 py-8">
          <div className="bg-card border border-border rounded-lg p-6">
            <h2 className="text-lg font-semibold text-foreground mb-4">Quick Navigation</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
              {sections?.map((section) => (
                <a
                  key={section?.id}
                  href={`#${section?.id}`}
                  className="flex items-center gap-2 text-sm text-accent hover:underline"
                >
                  <Icon name={section?.icon} size={16} />
                  {section?.title}
                </a>
              ))}
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 py-8">
          <div className="prose prose-slate dark:prose-invert max-w-none">
            {sections?.map((section) => (
              <section key={section?.id} id={section?.id} className="mb-12 scroll-mt-20">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 bg-accent/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Icon name={section?.icon} size={20} color="var(--color-accent)" />
                  </div>
                  <h2 className="text-2xl md:text-3xl font-bold text-foreground m-0">{section?.title}</h2>
                </div>
                
                <div className="space-y-4 text-muted-foreground">
                  {section?.content?.map((item, idx) => (
                    <div key={idx}>
                      {item?.subtitle && (
                        <h3 className="text-lg font-semibold text-foreground mt-6 mb-2">{item?.subtitle}</h3>
                      )}
                      {item?.text && <p className="leading-relaxed">{item?.text}</p>}
                      {item?.list && (
                        <ul className="list-disc list-inside space-y-2 ml-4">
                          {item?.list?.map((listItem, listIdx) => (
                            <li key={listIdx} className="leading-relaxed">{listItem}</li>
                          ))}
                        </ul>
                      )}
                    </div>
                  ))}
                </div>
              </section>
            ))}
          </div>
        </div>

        {/* Contact Section */}
        <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 py-8 pb-16">
          <div className="bg-gradient-to-r from-accent/10 to-accent/5 border border-accent/20 rounded-lg p-8">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-accent/20 rounded-lg flex items-center justify-center flex-shrink-0">
                <Icon name="Mail" size={24} color="var(--color-accent)" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-foreground mb-2">Contact Us</h2>
                <p className="text-muted-foreground mb-4">
                  If you have any questions, concerns, or requests regarding this Privacy Policy or our data practices, please contact us:
                </p>
                <div className="space-y-2 text-sm">
                  <p className="text-foreground">
                    <strong>Email:</strong>{' '}
                    <a href="mailto:privacy@joegraziano.ca" className="text-accent hover:underline">
                      privacy@joegraziano.ca
                    </a>
                  </p>
                  <p className="text-muted-foreground">
                    We will respond to your inquiry within 30 days.
                  </p>
                </div>
                <div className="mt-6 pt-6 border-t border-border">
                  <p className="text-sm text-muted-foreground">
                    For information about cookies, please visit our{' '}
                    <a href="/cookies" className="text-accent hover:underline">
                      Cookie Policy
                    </a>
                    .
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default PrivacyPolicy;